import type { SaveStatus } from '../../hooks/useCollaboration';

export type MapSwitchGuards = {
  hasUnsavedChanges: boolean;
  saveStatus: SaveStatus;
  mapGenPending: boolean;
  forceSlowPath?: boolean;
};

export function diagramId(d: { _id?: string; id?: string } | null | undefined): string {
  if (!d) return '';
  return String(d._id ?? d.id ?? '');
}

export function canUseLocalSheetCache(
  targetId: string,
  localSheets: Array<{ _id?: string; id?: string; is_finish?: boolean; swimlane?: string; line?: string }>,
  guards: MapSwitchGuards
): boolean {
  if (!targetId?.trim()) return false;
  if (guards.forceSlowPath) return false;
  if (guards.hasUnsavedChanges) return false;
  if (guards.mapGenPending) return false;
  if (guards.saveStatus === 'pending' || guards.saveStatus === 'saving') return false;

  const local = localSheets.find((s) => diagramId(s) === targetId);
  if (!local) return false;
  if (local.is_finish === false) return false;

  return Boolean(local.swimlane && local.line);
}

export function mergeSheetDetail(
  base: Record<string, unknown>,
  detail: Record<string, unknown>
): Record<string, unknown> {
  return {
    ...base,
    line: detail.line ?? base.line,
    swimlane: detail.swimlane ?? base.swimlane,
    second: detail.second ?? base.second,
    stakeholder_preview: detail.stakeholder_preview ?? base.stakeholder_preview,
    name: detail.name ?? base.name,
    is_finish: detail.is_finish ?? base.is_finish,
    updated_at: detail.updated_at ?? base.updated_at,
  };
}

export function hasCompleteLocalSheet(
  sheets: Array<{ _id?: string; id?: string; swimlane?: string; line?: string; is_finish?: boolean }>,
  targetId: string
): boolean {
  const local = sheets.find((s) => diagramId(s) === targetId);
  if (!local) return false;
  if (local.is_finish === false) return false;
  return Boolean(local.swimlane && local.line);
}

type SheetListItem = {
  _id?: string;
  id?: string;
  swimlane?: string;
  line?: string;
  second?: string;
  is_finish?: boolean;
};

/** Fusionne la liste process API avec le cache local (préserve feuilles complètes + notes enrichies). */
export function mergeProcessListWithLocalCache(
  existing: SheetListItem[],
  incoming: SheetListItem[],
): SheetListItem[] {
  if (existing.length === 0) return [...incoming];

  const existingItemsMap = new Map(
    existing.map((item, index) => [diagramId(item), { item, index }]),
  );

  const mergeOne = (local: SheetListItem, remote: SheetListItem): SheetListItem => {
    const id = diagramId(local);
    if (!hasCompleteLocalSheet([local], id)) return remote;

    const localHasPainNotes = swimlaneHasPainPointNotes(local.swimlane);
    const remoteHasPainNotes = swimlaneHasPainPointNotes(remote.swimlane);
    const remoteComplete = hasCompleteLocalSheet([remote], id);

    if ((localHasPainNotes && !remoteHasPainNotes) || !remoteComplete) {
      return {
        ...remote,
        swimlane: local.swimlane ?? remote.swimlane,
        second: local.second ?? remote.second,
        line: local.line ?? remote.line,
      };
    }
    return remote;
  };

  const updatedItems = existing.map((item) => {
    const sid = diagramId(item);
    const remote = incoming.find((candidate) => diagramId(candidate) === sid);
    if (!remote) return item;
    return mergeOne(item, remote);
  });

  const newItems = incoming.filter(
    (remote) => !existingItemsMap.has(diagramId(remote)),
  );

  return [...updatedItems, ...newItems];
}

/** True si le JSON swimlane contient déjà des notes source=pain_points. */
export function swimlaneHasPainPointNotes(swimlane: unknown): boolean {
  try {
    const blocks =
      typeof swimlane === 'string'
        ? JSON.parse(swimlane || '[]')
        : Array.isArray(swimlane)
          ? swimlane
          : [];
    if (!Array.isArray(blocks)) return false;
    return blocks.some(
      (b: any) =>
        b &&
        b.type === 'note' &&
        b.meta &&
        typeof b.meta === 'object' &&
        b.meta.source === 'pain_points'
    );
  } catch {
    return false;
  }
}

/** Feuille complète mais sans post-its → il faut GET calculate/new (enrich + matching). */
export function sheetNeedsPainPointsEnrich(sheet: {
  swimlane?: unknown;
  is_finish?: boolean;
} | null | undefined): boolean {
  if (!sheet || sheet.is_finish === false) return false;
  if (!sheet.swimlane) return false;
  return !swimlaneHasPainPointNotes(sheet.swimlane);
}

export type SheetUrlResolveAction = 'useLocal' | 'keepCurrent' | 'fetchRemote' | 'skip';

export type ResolveSheetOnUrlChangeInput = {
  urlSheetId: string;
  currentSheetId: string;
  pendingSheetId: string | null;
  suppressFetchForSheetId: string | null;
  localSheets: Array<{ _id?: string; id?: string; swimlane?: string; line?: string; is_finish?: boolean }>;
  currentHasPayload: boolean;
};

export type SheetUrlResolveResult = {
  action: SheetUrlResolveAction;
  localSheetId?: string;
};

/**
 * Décide comment synchroniser l'état affiché quand l'URL (sId) change ou qu'une navigation locale est en cours.
 * Évite la race où l'onglet B est sélectionné mais l'URL pointe encore vers A.
 */
export function resolveSheetOnUrlChange(input: ResolveSheetOnUrlChangeInput): SheetUrlResolveResult {
  const {
    urlSheetId,
    currentSheetId,
    pendingSheetId,
    suppressFetchForSheetId,
    localSheets,
    currentHasPayload,
  } = input;

  if (!urlSheetId) {
    return { action: 'skip' };
  }

  if (suppressFetchForSheetId === urlSheetId) {
    if (currentSheetId === urlSheetId && currentHasPayload) {
      return { action: 'skip' };
    }
    if (hasCompleteLocalSheet(localSheets, urlSheetId)) {
      return { action: 'useLocal', localSheetId: urlSheetId };
    }
    return { action: 'fetchRemote' };
  }

  if (pendingSheetId && pendingSheetId !== urlSheetId) {
    return { action: 'keepCurrent' };
  }

  const stateMatches = currentSheetId === urlSheetId && currentHasPayload;
  if (stateMatches) {
    return { action: 'skip' };
  }

  if (hasCompleteLocalSheet(localSheets, urlSheetId)) {
    return { action: 'useLocal', localSheetId: urlSheetId };
  }

  return { action: 'fetchRemote' };
}

/** Évite GET /mapping/process/... au switch si la feuille cible est déjà en cache local. */
export function shouldReloadProcessListOnSwitch(
  targetId: string,
  localSheets: Array<{ _id?: string; id?: string }>,
  forceSlowPath?: boolean
): boolean {
  if (forceSlowPath) return true;
  if (!targetId?.trim()) return true;
  return !localSheets.some((sheet) => diagramId(sheet) === targetId);
}

/** setMap a déjà planifié les side-effects approval — ne pas les dupliquer dans l'effet sId. */
export function shouldSkipApprovalForNavigation(
  skipApprovalForMapId: string | null,
  urlMapId: string
): boolean {
  return Boolean(skipApprovalForMapId && skipApprovalForMapId === urlMapId);
}
